import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api';

const ResetPassword: React.FC = () => {
  const { resettoken } = useParams<{ resettoken: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token || user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Validate token on mount
  useEffect(() => {
    if (!resettoken) {
      setError('Invalid reset token. Please use the link from your email.');
      setIsValidatingToken(false);
    } else {
      setIsValidatingToken(false);
    }
  }, [resettoken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!password || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!resettoken) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.put(
        `${API_URL}/auth/reset-password/${resettoken}`,
        { password },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setSuccess(true);
        // Store token and redirect to dashboard after 2 seconds
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        setError(response.data.error || 'Failed to reset password. Please try again.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        err.response?.data?.message || 
        'Failed to reset password. The link may have expired. Please request a new one.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication
  const token = localStorage.getItem('token');
  if (token || user) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
      >
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  // Don't show loading if we already know there's an error
  if (isValidatingToken && resettoken) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
      >
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      {/* Header */}
      <header className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
        </Link>
        <Link
          to="/login"
          className="text-sm text-white/70 hover:text-white transition-colors"
        >
          Back to login
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-md w-full">
          <div 
            className="bg-white/5 backdrop-blur rounded-3xl p-8 border border-white/10 shadow-2xl"
            style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
          >
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
              <p className="text-white/70">
                {success 
                  ? 'Password reset successful! Redirecting to dashboard...'
                  : 'Enter your new password below'}
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {success ? (
              <div className="space-y-6">
                <div className="rounded-lg border border-green-400/50 bg-green-500/20 px-4 py-3 text-sm text-green-300">
                  Password reset successful! You will be redirected to the dashboard shortly.
                </div>
              </div>
            ) : !resettoken ? (
              <div className="space-y-6">
                <div className="rounded-lg border border-yellow-400/50 bg-yellow-500/20 px-4 py-3 text-sm text-yellow-300">
                  Invalid reset link. Please check your email for the correct password reset link, or request a new one.
                </div>
                <div className="text-center">
                  <Link 
                    to="/forgot-password" 
                    className="btn-adsmash-primary inline-flex items-center justify-center rounded-lg px-6 py-3 text-white font-semibold shadow-lg transition hover:opacity-90"
                  >
                    Request New Reset Link
                  </Link>
                </div>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password"
                    className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-adsmash-primary w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-white font-semibold shadow-lg transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="font-medium text-white/70 hover:text-white transition-colors">
                Remember your password? Sign in
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPassword;

